import { useEffect, useId, useState, type Ref } from 'react'
import { api } from '../servizi/api.ts'

/**
 * «Nome utente o email» con il dominio accanto (decisioni di Davide dell'1/11/2026, 520).
 *
 * Chi ha la casella CIDIS scrive solo «dramondetti» e il dominio viene dal menu accanto: i domini
 * sono quelli di `accesso.domini` (GET /auth/domini), il primo per difetto. Con una voce sola il menu
 * c'e' ma e' statico. Chi scrive una chiocciola (un mediatore con la sua email) entra con l'indirizzo
 * scritto per intero, e il menu si nasconde. L'ultimo dominio scelto lo ricorda solo questo browser:
 * se la memoria del browser non e' disponibile vale il primo.
 *
 * Lo usano la pagina di accesso (password, collegamento, codice) e «Ho dimenticato la password».
 */

const CHIAVE_MEMORIA = 'cidis.accesso.dominio'

/** Il dominio se l'API non risponde: la pagina di accesso deve funzionare lo stesso. */
const DOMINIO_DI_RISERVA = 'cidisonlus.org'

function dominioRicordato(): string | null {
  try { return window.localStorage.getItem(CHIAVE_MEMORIA) } catch { return null }
}

function ricordaDominio(dominio: string) {
  try { window.localStorage.setItem(CHIAVE_MEMORIA, dominio) } catch { /* memoria del browser non disponibile */ }
}

/** Domini di CIDIS e quello scelto: l'ultimo usato in questo browser, altrimenti il primo. */
export function useDominio() {
  const [domini, setDomini] = useState<string[]>([DOMINIO_DI_RISERVA])
  const [dominio, setDominio] = useState<string>(() => dominioRicordato() ?? DOMINIO_DI_RISERVA)
  useEffect(() => {
    api.get<{ domini: string[] }>('/auth/domini')
      .then((r) => {
        if (!r.domini.length) return
        setDomini(r.domini)
        setDominio((d) => (r.domini.includes(d) ? d : r.domini[0]))
      })
      .catch(() => {})
  }, [])
  const scegli = (d: string) => { setDominio(d); ricordaDominio(d) }
  return { domini, dominio, scegli }
}

/** L'indirizzo da mandare: quello scritto, se ha la chiocciola; altrimenti il nome utente con il dominio. */
export function indirizzoCompleto(scritto: string, dominio: string): string {
  const valore = scritto.trim()
  return !valore || valore.includes('@') ? valore : `${valore}@${dominio}`
}

export function CampoNomeUtente({ valore, cambia, domini, dominio, scegliDominio, riferimento, etichetta = 'Nome utente o email' }: {
  valore: string
  cambia: (v: string) => void
  domini: string[]
  dominio: string
  scegliDominio: (d: string) => void
  riferimento?: Ref<HTMLInputElement>
  etichetta?: string
}) {
  const id = useId()
  const conChiocciola = valore.includes('@')
  return (
    <div className="campo">
      <label htmlFor={id}>
        {etichetta}<span className="campo-obbligatorio" aria-hidden="true"> *</span>
      </label>
      <div className={`campo-nome-utente${conChiocciola ? ' intero' : ''}`}>
        <input id={id} type="text" value={valore} onChange={(e) => cambia(e.target.value)} ref={riferimento}
               autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email"
               required aria-describedby={`${id}-nota`} />
        {!conChiocciola && (domini.length > 1
          ? (
            <select value={dominio} onChange={(e) => scegliDominio(e.target.value)} aria-label="Dominio">
              {domini.map((d) => <option key={d} value={d}>@{d}</option>)}
            </select>
          )
          : <span className="campo-dominio" aria-label={`Dominio: ${dominio}`}>@{dominio}</span>)}
      </div>
      <p className="campo-aiuto" id={`${id}-nota`}>
        {conChiocciola ? 'Entri con l’indirizzo scritto per intero.' : 'Con la casella CIDIS basta il nome utente.'}
      </p>
    </div>
  )
}
